import React from 'react'
import { Outlet } from 'react-router-dom'

import { ProfileRootPageDocument } from 'generated/resources/graphql'
import { keyBy } from 'lodash'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import RegularAccessBanner from 'modules/employees/components/blocks/RegularAccessBanner'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import CollectionsBootstrap from 'modules/profile/lib/CollectionsBootstrap'

export interface RouteContext {
  canEditPersonalInfo: boolean
  canSeeCustomDimensions: boolean
  canSeePerformance: boolean
  canSeePermissions: boolean
  canSeePersonalInfo: boolean
  canSeeOverview: boolean
  cardEmployeeRight: React.MutableRefObject<HTMLElement | null>
  headerTitle: React.MutableRefObject<HTMLElement | null>
  permissionGroupName: string
  permissionGroupId: string
  seeOldCostCentersAssignments: boolean
}

const Container = observer(() => {
  const {
    employee: { id: employeeId },
    company,
  } = useCurrentSession()
  const cardEmployeeRight = React.useRef<HTMLElement | null>(null)
  const headerTitle = React.useRef<HTMLElement | null>(null)

  const { data } = useQuery(ProfileRootPageDocument, {
    variables: { id: employeeId, companyId: company.id },
  })

  const employeeData = data?.employees.employee
  const features = keyBy(data?.features?.featuresConnection?.nodes, 'name')
  const canSeeCustomDimensions = features['dev_finance_show_custom_dimensions']?.enabled ?? false
  const seeOldCostCentersAssignments =
    features['see_old_cost_centers_assignments']?.enabled ?? false

  const canSeePersonalInfo = data?.employees.employee?._policies?.readPersonalInfo.can ?? false
  const canEditPersonalInfo =
    data?.employees.employee?.access.personalInfo?._policies?.edit.can ?? false
  // we are only showing the permission group in the employees page, but not in the self profile
  const canSeePermissions = false
  const permissionGroupName = ''
  const permissionGroupId = ''

  const employeeGraphqlPolicies = data?.employees.employee?._policies
  const canSeePerformance = employeeGraphqlPolicies?.seeEmployeePerformanceTab.can ?? false
  const canSeeFutureEmployees = data?.employees._policies?.seeFutureEmployees.can ?? false

  const context: RouteContext = {
    canEditPersonalInfo,
    canSeeCustomDimensions,
    canSeePerformance,
    canSeePermissions,
    canSeePersonalInfo,
    canSeeOverview: true,
    cardEmployeeRight,
    headerTitle,
    permissionGroupName,
    permissionGroupId,
    seeOldCostCentersAssignments,
  }

  return (
    <ResponsivePage.Body variant="fullscreen" showHeader={false}>
      {employeeData && (
        <RegularAccessBanner
          employee={employeeData}
          canSeeFutureEmployees={canSeeFutureEmployees}
        />
      )}
      <Outlet context={context} />
    </ResponsivePage.Body>
  )
})

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}

export default RootPage
